import React, { useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import { useStores } from "_common/hooks";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper";
import { PriceApi } from "states/api/price";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { formatNumber } from "_common/utils/formatValue";
import classNames from "classnames";
import i18next from "i18next";

import icon_deposit from "assets/icons/icon_deposit.svg";
import icon_support from "assets/icons/icon_support.svg";
import icon_volume from "assets/icons/icon_volume.svg";
import icon_bars from "assets/icons/icon_bars.svg";

import icon_help from "assets/icons/icon_help.svg";
import icon_question from "assets/icons/icon_question.svg";
import icon_diamon from "assets/icons/icon_diamon.svg";
import icon_rank from "assets/icons/icon_rank.svg";
import icon_share from "assets/icons/icon_share.svg";
import icon_usdt from "assets/icons/icon_usdt.svg";
import icon_robot from "assets/icons/icon_robot.svg";
import icon_earn from "assets/icons/icon_earn.svg";

import HeaderHome from "_common/component/Layout/Header/HeaderHome";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight, faBars } from "@fortawesome/free-solid-svg-icons";

const HomePage = observer(() => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const {
    authStore: { user, banners, top_coin, games, authenticated, logo, general },
  } = useStores();

  const { data, refetch } = useQuery(
    ["prices", "list"],
    () =>
      PriceApi.getList({
        params: {
          page: 1,
          limit: 10,
          ...params,
        },
      }),
    {
      staleTime: 300000,
    }
  );

  useEffect(() => {
    if (!authenticated) return;
    queryClient.invalidateQueries(["get_profile"]);
  }, [authenticated]);

  useEffect(() => {
    const interval = setInterval(() => {
      refetch();
    }, 30000);

    return () => {
      clearInterval(interval);
    };
  }, []);

  const coinTopPrices = useMemo(
    () =>
      top_coin.map((item) => {
        const price = data?.find((obj) => obj?.symbol === item?.code);

        return { ...item, price };
      }),
    [top_coin, data]
  );

  return (
    <div
      className="relative w-full mx-auto overflow-auto "
      style={{ height: "calc(100% - 63px)" }}
    >
      <HeaderHome />
      <div className="w-full overflow-hidden pt-[72px]">
        <div className="flex">
          <Swiper
            spaceBetween={30}
            centeredSlides
            autoplay={{
              delay: 5000,
              disableOnInteraction: false,
            }}
            modules={[Autoplay]}
            className="swiper2"
          >
            {banners.map((item) => (
              <SwiperSlide key={item?.id}>
                {item.type === "image" && (
                  <img alt="" src={item?.image_url} height="170" />
                )}
                {item.type === "video" && (
                  <iframe
                    width="430"
                    height="287"
                    src={item?.src}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    title="video"
                  />
                )}
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
        <div>
          <div className="flex gap-4 p-4">
            <Link
              className=" flex-1 flex items-center justify-between px-4 py-2 rounded bg-[#202125] hover:bg-[#121212] duration-200 ease-in-out"
              to="/member/recharge"
            >
              <div className="flex items-center gap-3">
                <img src={icon_deposit} className="w-[40px] h-[40px]" alt="" />
                <span className="text-[#fff]">{i18next.t("deposit")}</span>
              </div>
              <FontAwesomeIcon fontSize={18} icon={faArrowRight} />
            </Link>
            <Link
              className=" flex-1 flex items-center justify-between px-4 py-2 rounded bg-[#3D5AFE] hover:bg-[#2a3eb1] duration-200 ease-in-out"
              to="/cskh"
            >
              <div className="flex items-center gap-3">
                <div className="relative">
                  <img
                    src={icon_support}
                    className="w-[40px] h-[40px]"
                    alt=""
                  />
                </div>
                <span className="text-[#fff]">{i18next.t("cskh")}</span>
                {user?.chat_count > 0 && (
                  <span className="absolute left-2 top-2 w-[15px] h-[15px] text-center leading-[15px] text-[10px] rounded-[50%] bg-red-600 text-white">
                    {formatNumber(user?.chat_count)}
                  </span>
                )}
              </div>
              <FontAwesomeIcon fontSize={18} icon={faArrowRight} />
            </Link>
          </div>
        </div>
        <div className="flex flex-col gap-2 p-4">
          <h5 className="text-[24px] text-white">
            {i18next.t("popular_coin")}
          </h5>
          <div className="mt-[0.5rem]">
            <div className="top-coin">
              {coinTopPrices?.map((item) => (
                <Link
                  key={item?.code}
                  to={`/contract?symbol=${item?.code}`}
                  className="item"
                >
                  <h5>
                    <b className="text-[#fff]">{item?.name?.split("/")?.[0]}</b>
                    /<span>{item?.name?.split("/")?.[1]}</span>
                  </h5>
                  <div>
                    <h4
                      className={classNames("price", {
                        "text-green": item?.price?.priceChangePercent > 0,
                        "text-red": item?.price?.priceChangePercent <= 0,
                      })}
                    >
                      {formatNumber(item?.price?.lastPrice, "0,0.[00000]")}
                    </h4>
                    <p
                      className={classNames({
                        "text-green": item?.price?.priceChangePercent > 0,
                        "text-red": item?.price?.priceChangePercent <= 0,
                      })}
                    >
                      {formatNumber(
                        item?.price?.priceChangePercent,
                        "0,0.[00]"
                      )}
                      %
                    </p>
                  </div>
                  <div className="flex items-center gap-1 text-[#fff]">
                    <span>{i18next.t("trade")}</span>
                    <FontAwesomeIcon fontSize={16} icon={faArrowRight} />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
        <div className="flex justify-between items-center px-4 ">
          <img src={icon_volume} className="w-[25px] h-[24px]" alt="" />
          <Link to="/board">
            <img src={icon_bars} className="w-[25px] h-[24px]" alt="" />
          </Link>
        </div>
        <div className="grid grid-cols-4 bg-[#121212] pb-8 mb-10">
          <div className="flex item-center justify-center pt-2 cursor-pointer">
            <Link
              to="/news"
              className="p-2 rounded hover:bg-[#1c1c1e] flex flex-col items-center justify-start gap-1"
            >
              <img src={icon_help} alt="" className="w-[40px] h-[40px]" />
              <span className="text-[#fff] font-medium text-center">
                {i18next.t("help")}
              </span>
            </Link>
          </div>
          <div className="flex item-center justify-center pt-2 cursor-pointer">
            <Link
              to="/help"
              className="p-2 rounded hover:bg-[#1c1c1e] flex flex-col items-center justify-start gap-1"
            >
              <img src={icon_question} alt="" className="w-[40px] h-[40px]" />
              <span className="text-[#fff] font-medium text-center">
                {i18next.t("support_center")}
              </span>
            </Link>
          </div>
          <div className="flex item-center justify-center pt-2 cursor-pointer">
            <Link
              to="/vip"
              className="p-2 rounded hover:bg-[#1c1c1e] flex flex-col items-center justify-start gap-1"
            >
              <img src={icon_diamon} alt="" className="w-[40px] h-[40px]" />
              <span className="text-[#fff] font-medium text-center">
                {i18next.t("vip")}
              </span>
            </Link>
          </div>
          <div className="flex item-center justify-center pt-2 cursor-pointer">
            <Link
              to="/rank"
              className="p-2 rounded hover:bg-[#1c1c1e] flex flex-col items-center justify-start gap-1"
            >
              <img src={icon_rank} alt="" className="w-[40px] h-[40px]" />
              <span className="text-[#fff] font-medium text-center">
                {i18next.t("rank")}
              </span>
            </Link>
          </div>
          <div className="flex item-center justify-center pt-2 cursor-pointer">
            <Link
              to="/invite"
              className="p-2 rounded hover:bg-[#1c1c1e] flex flex-col items-center justify-start gap-1"
            >
              <img src={icon_share} alt="" className="w-[40px] h-[40px]" />
              <span className="text-[#fff] font-medium text-center">
                {i18next.t("invite")}
              </span>
            </Link>
          </div>
          <div className="flex item-center justify-center pt-2 cursor-pointer">
            <Link
              to="/saving"
              className="p-2 rounded hover:bg-[#1c1c1e] flex flex-col items-center justify-start gap-1"
            >
              <img src={icon_usdt} alt="" className="w-[40px] h-[40px]" />
              <span className="text-[#fff] font-medium text-center">
                {i18next.t("saving")}
              </span>
            </Link>
          </div>
          <div className="flex item-center justify-center pt-2 cursor-pointer">
            <Link
              to="/coming-soon"
              className="p-2 rounded hover:bg-[#1c1c1e] flex flex-col items-center justify-start gap-1"
            >
              <img src={icon_robot} alt="" className="w-[40px] h-[40px]" />
              <span className="text-[#fff] font-medium text-center">
                {i18next.t("robto_trade")}
              </span>
            </Link>
          </div>
          <div className="flex item-center justify-center pt-2 cursor-pointer">
            <Link
              to="/coming-soon"
              className="p-2 rounded hover:bg-[#1c1c1e] flex flex-col items-center justify-start gap-1"
            >
              <img src={icon_earn} alt="" className="w-[40px] h-[40px]" />
              <span className="text-[#fff] font-medium text-center">
                {i18next.t("mining")}
              </span>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
});

export default HomePage;
